import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { pageAccueil } from '../utils/pageAccueil';
import Layout from '../components/Layout';
import { Lock, Eye, EyeOff, CheckCircle, XCircle, Shield } from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';
const VERT  = '#1B5E20';
const ROUGE = '#C62828';

// ─── Analyser la force du mot de passe ───────────────────────────────────
function analyserMdp(pwd) {
  const regles = [
    { id: 'len',     label: 'Au moins 8 caractères',       ok: pwd.length >= 8 },
    { id: 'upper',   label: 'Une lettre majuscule',         ok: /[A-Z]/.test(pwd) },
    { id: 'digit',   label: 'Un chiffre',                   ok: /[0-9]/.test(pwd) },
    { id: 'special', label: 'Un caractère spécial (!@#$%…)',ok: /[^A-Za-z0-9]/.test(pwd) },
  ];
  const score = regles.filter(r => r.ok).length;
  let force = 'faible', couleur = ROUGE, largeur = 25;
  if (score === 2) { force = 'faible';  couleur = '#E65100'; largeur = 40; }
  if (score === 3) { force = 'moyen';   couleur = '#F9A825'; largeur = 65; }
  if (score === 4) { force = 'fort';    couleur = VERT;      largeur = 100; }
  return { regles, score, force, couleur, largeur };
}

export default function ChangerMotDePassePage() {
  const navigate    = useNavigate();
  const location    = useLocation();
  const { employe, rafraichir, remplacerJeton } = useAuth();
  const premierConnexion = location.state?.premierConnexion === true;
  // En-tête réduit tant que le changement est imposé (première connexion) : aucune autre page n'est accessible.
  const enteteMinimal = premierConnexion || employe?.premier_connexion === true;

  const [ancienMdp,   setAncienMdp]   = useState('');
  const [nouveauMdp,  setNouveauMdp]  = useState('');
  const [confirmMdp,  setConfirmMdp]  = useState('');
  const [showAncien,  setShowAncien]  = useState(false);
  const [showNouv,    setShowNouv]    = useState(false);
  const [showConf,    setShowConf]    = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [succes,      setSucces]      = useState(false);

  const analyse = analyserMdp(nouveauMdp);
  const token   = localStorage.getItem('sogas_token');

  // Rediriger si pas connecté
  useEffect(() => {
    if (!token) navigate('/login');
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (nouveauMdp !== confirmMdp) {
      setError('Les deux mots de passe ne correspondent pas.');
      return;
    }
    if (analyse.score < 4) {
      setError('Le mot de passe ne respecte pas toutes les règles de sécurité.');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/auth/changer-mot-de-passe`,
        {
          // Toujours exigé, y compris le mot de passe provisoire en première connexion.
          ancien_password: ancienMdp,
          nouveau_password: nouveauMdp,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Le serveur refuse désormais tout jeton émis avant le changement : le nouveau jeton
      // remplace l'ancien pour rester connecté.
      if (res.data?.token && remplacerJeton) remplacerJeton(res.data.token);
      setSucces(true);
      // Profil rechargé : « première connexion » terminée (sinon un prestataire externe serait
      // renvoyé vers cette page par la route protégée).
      if (rafraichir) await rafraichir();
      setTimeout(() => {
        if (!employe) { navigate('/login'); return; }
        navigate(pageAccueil(employe));
      }, 2500);
    } catch (err) {
      const data = err.response?.data;
      if (data?.erreurs?.length > 0) {
        setError(data.erreurs.join(' · '));
      } else {
        setError(data?.message || 'Erreur lors du changement');
      }
    } finally {
      setLoading(false);
    }
  };

  if (succes) {
    return (
      <Layout minimal={enteteMinimal}>
      <div style={styles.page}>
        <div style={{ ...styles.card, textAlign: 'center', padding: '48px 32px' }}>
          <CheckCircle size={56} color={VERT} style={{ marginBottom: 16 }} />
          <div style={{ fontSize: 20, fontWeight: 800, color: VERT, marginBottom: 8 }}>
            Mot de passe changé !
          </div>
          <div style={{ fontSize: 13, color: '#666' }}>Redirection en cours…</div>
        </div>
      </div>
      </Layout>
    );
  }

  return (
    <Layout minimal={enteteMinimal}>
    <div style={styles.page}>
      <div style={styles.wrapper}>

        {/* ── En-tête ─────────────────────────────────────────────────────── */}
        <div style={styles.header}>
          <div style={styles.iconWrap}><Shield size={28} color="#fff" /></div>
          <div>
            <div style={styles.headerTitle}>
              {premierConnexion ? 'Bienvenue ! Créez votre mot de passe' : 'Changer le mot de passe'}
            </div>
            <div style={styles.headerSub}>
              {premierConnexion
                ? 'Pour des raisons de sécurité, vous devez créer un mot de passe personnel avant de continuer.'
                : 'Entrez votre ancien mot de passe puis choisissez-en un nouveau.'}
            </div>
          </div>
        </div>

        {/* ── Message première connexion ───────────────────────────────────── */}
        {premierConnexion && (
          <div style={styles.alertInfo}>
            <Shield size={16} color="#1565C0" style={{ flexShrink: 0 }} />
            <span>
              Votre compte utilise encore un <strong>mot de passe temporaire</strong>.
              Ce changement est <strong>obligatoire</strong> pour accéder à l'application.
            </span>
          </div>
        )}

        <div style={styles.card}>
          <form onSubmit={handleSubmit}>

            {/* ── Ancien mot de passe ──────────────────────────────────────── */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                {premierConnexion ? 'Mot de passe temporaire reçu' : 'Ancien mot de passe'}
              </label>
              <FieldMdp
                value={ancienMdp} onChange={setAncienMdp}
                show={showAncien} onToggle={() => setShowAncien(v => !v)}
                placeholder="Votre mot de passe actuel"
              />
            </div>

            {/* ── Nouveau mot de passe ─────────────────────────────────────── */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Nouveau mot de passe</label>
              <FieldMdp
                value={nouveauMdp} onChange={setNouveauMdp}
                show={showNouv} onToggle={() => setShowNouv(v => !v)}
                placeholder="Minimum 8 caractères"
              />
              {/* Barre de force */}
              {nouveauMdp.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 11, color: '#888' }}>Force du mot de passe</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: analyse.couleur, textTransform: 'uppercase' }}>
                      {analyse.force}
                    </span>
                  </div>
                  <div style={{ height: 6, background: '#F0F0F0', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', width: `${analyse.largeur}%`,
                      background: analyse.couleur, borderRadius: 3,
                      transition: 'width 0.3s, background 0.3s',
                    }} />
                  </div>
                </div>
              )}
            </div>

            {/* ── Règles de sécurité ───────────────────────────────────────── */}
            {nouveauMdp.length > 0 && (
              <div style={styles.regles}>
                {analyse.regles.map(r => (
                  <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    {r.ok
                      ? <CheckCircle size={13} color={VERT} />
                      : <XCircle size={13} color="#ccc" />
                    }
                    <span style={{ fontSize: 12, color: r.ok ? VERT : '#999', fontWeight: r.ok ? 600 : 400 }}>
                      {r.label}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* ── Confirmation ─────────────────────────────────────────────── */}
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Confirmer le nouveau mot de passe</label>
              <FieldMdp
                value={confirmMdp} onChange={setConfirmMdp}
                show={showConf} onToggle={() => setShowConf(v => !v)}
                placeholder="Répétez le nouveau mot de passe"
                invalid={confirmMdp.length > 0 && confirmMdp !== nouveauMdp}
              />
              {confirmMdp.length > 0 && confirmMdp !== nouveauMdp && (
                <div style={{ fontSize: 11, color: ROUGE, marginTop: 4 }}>Les mots de passe ne correspondent pas</div>
              )}
              {confirmMdp.length > 0 && confirmMdp === nouveauMdp && (
                <div style={{ fontSize: 11, color: VERT, marginTop: 4, display:'flex', alignItems:'center', gap:4 }}>
                  <CheckCircle size={11} />Correspondance confirmée
                </div>
              )}
            </div>

            {/* ── Erreur ──────────────────────────────────────────────────── */}
            {error && (
              <div style={styles.errorBox}>
                <XCircle size={14} />
                <span>{error}</span>
              </div>
            )}

            {/* ── Bouton ──────────────────────────────────────────────────── */}
            <button
              type="submit"
              disabled={loading || analyse.score < 4 || confirmMdp !== nouveauMdp || !ancienMdp}
              style={{
                width: '100%', height: 46, background: VERT, color: '#fff',
                border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700,
                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                opacity: (loading || analyse.score < 4 || confirmMdp !== nouveauMdp || !ancienMdp) ? 0.5 : 1,
                transition: 'opacity 0.2s',
              }}
            >
              <Lock size={16} />
              {loading ? 'Enregistrement…' : 'Valider le nouveau mot de passe'}
            </button>

            {/* ── Lien retour (si pas première connexion) ─────────────────── */}
            {!premierConnexion && (
              <button
                type="button"
                onClick={() => navigate(-1)}
                style={{ width:'100%', marginTop: 10, background:'none', border:'none',
                  color:'#999', fontSize:12, cursor:'pointer', textDecoration:'underline' }}
              >
                Annuler
              </button>
            )}
          </form>
        </div>

        <div style={{ textAlign:'center', fontSize:10, color:'#ccc', marginTop:12 }}>
          © 2026 SOGAS · Abdoul Aziz Talla
        </div>
      </div>
    </div>
    </Layout>
  );
}

// ── Composant champ mot de passe ──────────────────────────────────────────
function FieldMdp({ value, onChange, show, onToggle, placeholder, invalid }) {
  return (
    <div style={{
      display:'flex', alignItems:'center',
      border: `1.5px solid ${invalid ? ROUGE : (value ? '#1B5E20' : '#E5E5E5')}`,
      borderRadius: 10, padding:'0 12px', background:'#FAFAFA', transition:'border 0.15s',
    }}>
      <Lock size={14} color="#bbb" style={{ marginRight: 8, flexShrink:0 }} />
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          border:'none', background:'transparent', padding:'11px 0',
          fontSize:13, outline:'none', width:'100%', color:'#222',
        }}
        autoComplete="new-password"
      />
      <button type="button" onClick={onToggle}
        style={{ background:'none', border:'none', cursor:'pointer', padding:'4px', display:'flex', alignItems:'center' }}>
        {show ? <EyeOff size={14} color="#bbb" /> : <Eye size={14} color="#bbb" />}
      </button>
    </div>
  );
}

const styles = {
  page: {
    minHeight: 'calc(100vh - 70px)', background: 'var(--sg-fond)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 16, fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  wrapper: {
    width: '100%', maxWidth: 460,
  },
  header: {
    display: 'flex', alignItems: 'flex-start', gap: 14,
    marginBottom: 16,
  },
  iconWrap: {
    width: 48, height: 48, borderRadius: 12,
    background: '#1B5E20', display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  headerTitle: {
    fontWeight: 800, fontSize: 18, color: '#1a1a1a',
  },
  headerSub: {
    fontSize: 12, color: '#888', marginTop: 4, lineHeight: 1.5,
  },
  alertInfo: {
    background: '#E3F2FD', border: '1px solid #90CAF9',
    borderRadius: 8, padding: '10px 14px',
    display: 'flex', alignItems: 'flex-start', gap: 8,
    fontSize: 12, color: '#1565C0', marginBottom: 14, lineHeight: 1.5,
  },
  card: {
    background: '#fff', borderRadius: 14, padding: '24px 22px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.07)',
  },
  fieldGroup: {
    marginBottom: 16,
  },
  label: {
    display: 'block', fontSize: 11, fontWeight: 700, color: '#555',
    marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5,
  },
  regles: {
    background: '#F9F9F9', borderRadius: 8, padding: '10px 14px',
    marginBottom: 16, border: '1px solid #EEEEEE',
  },
  errorBox: {
    background: '#FFF0F0', border: '1px solid #EF9A9A',
    borderRadius: 8, padding: '10px 14px', color: '#C62828',
    fontSize: 12, display: 'flex', alignItems: 'center', gap: 8,
    marginBottom: 14,
  },
};
